import {
  useCallback,
  useMemo,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { Button, Icon, PluginViewport, Stack, Text, tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';
import type { PointCloudRoute } from './model';

interface PointCloudPaneHostProps {
  route: PointCloudRoute;
  defenceVariantActive: boolean;
  ariaLabel: string;
  description: string;
  stateNarration: string;
  toolbar: ReactNode;
  bottomSlot: ReactNode;
  onZoom(step: number): void;
  onPan(stepLon: number, stepLat: number): void;
  onOpenExternalViewer(): void;
  children?: ReactNode;
}

export function PointCloudPaneHost({
  route,
  defenceVariantActive,
  ariaLabel,
  description,
  stateNarration,
  toolbar,
  bottomSlot,
  onZoom,
  onPan,
  onOpenExternalViewer,
  children,
}: PointCloudPaneHostProps) {
  const liveRegion = useLiveRegion();
  const announceMode = useCallback(() => {
    liveRegion.announce('status', 'Point cloud keyboard mode active. Press question mark for shortcut help.');
  }, [liveRegion]);

  const pluginMount = useMemo(
    () =>
      route === 'plugin-potree'
        ? async (container: HTMLDivElement) => {
            container.innerHTML = '';
            const message = document.createElement('div');
            message.style.display = 'grid';
            message.style.placeItems = 'center';
            message.style.width = '100%';
            message.style.height = '100%';
            message.style.color = tokens.color.ink.inverse;
            message.textContent = 'Potree plugin viewport placeholder';
            container.appendChild(message);
            return () => {
              container.innerHTML = '';
            };
          }
        : null,
    [route],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      const isTypingTarget = target.closest('input,textarea,select,[contenteditable="true"]');
      if (isTypingTarget) return;
      switch (event.key) {
        case '+':
        case '=':
          event.preventDefault();
          onZoom(1);
          return;
        case '-':
        case '_':
          event.preventDefault();
          onZoom(-1);
          return;
        case 'ArrowUp':
          event.preventDefault();
          onPan(0, 1);
          return;
        case 'ArrowDown':
          event.preventDefault();
          onPan(0, -1);
          return;
        case 'ArrowLeft':
          event.preventDefault();
          onPan(-1, 0);
          return;
        case 'ArrowRight':
          event.preventDefault();
          onPan(1, 0);
          return;
        default:
          return;
      }
    },
    [onPan, onZoom],
  );

  return (
    <div
      role="application"
      aria-label={ariaLabel}
      aria-describedby="pointcloud-description-region pointcloud-state-region"
      tabIndex={0}
      onFocus={announceMode}
      onKeyDown={onKeyDown}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: tokens.zIndex.panel,
        background:
          route === 'cesium-pnts' ? 'transparent' : tokens.color.canvas.light ?? tokens.color.surface[2],
        display: 'grid',
        gridTemplateRows: 'auto minmax(0, 1fr) auto',
      }}
    >
      <div
        role="toolbar"
        aria-label="Point cloud controls"
        style={{
          minHeight: tokens.density.touchMin,
          borderBottom: tokens.border.thin,
          background: tokens.color.surface[1],
          padding: tokens.space[2],
        }}
      >
        {toolbar}
      </div>

      <div
        style={{
          position: 'relative',
          minHeight: 0,
          pointerEvents: route === 'cesium-pnts' ? 'none' : 'auto',
        }}
      >
        {route === 'plugin-potree' ? (
          <PluginViewport
            id="plugin-potree"
            mountFn={pluginMount}
            aria-label="Potree plugin viewport"
            fallback={(
              <Text as="p" variant="body" style={{ margin: 0 }}>
                Potree plugin viewport is unavailable.
              </Text>
            )}
          />
        ) : route === 'cesium-pnts' ? (
          <div style={{ width: '100%', height: '100%' }}>
            {children}
          </div>
        ) : (
          <div
            role="status"
            aria-live="polite"
            style={{
              display: 'grid',
              placeItems: 'center',
              width: '100%',
              height: '100%',
              padding: tokens.space[5],
            }}
          >
            <Stack direction="column" align="center" gap={2}>
              <Icon name="cloud_off" size={24} decorative />
              <Text as="p" variant="body" style={{ margin: 0 }}>
                Point-cloud viewport is unavailable in this workspace.
              </Text>
              <Button variant="secondary" onClick={onOpenExternalViewer}>
                <Icon name="open_in_new" size={20} decorative />
                <span>Open Point Cloud Viewer</span>
              </Button>
            </Stack>
          </div>
        )}
      </div>

      <div
        style={{
          borderTop: tokens.border.thin,
          background: tokens.color.surface[1],
          padding: tokens.space[2],
        }}
      >
        <Stack direction="column" gap={1}>
          {bottomSlot}
          {defenceVariantActive ? (
            <Text as="p" variant="caption" style={{ margin: 0 }}>
              Defence variant active: plugin features may be reduced.
            </Text>
          ) : null}
        </Stack>
      </div>

      <div
        id="pointcloud-description-region"
        role="region"
        aria-label="Point cloud description"
        aria-live="polite"
        aria-atomic="false"
        style={{
          position: 'absolute',
          width: tokens.visuallyHidden.size,
          height: tokens.visuallyHidden.size,
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        {description}
      </div>
      <div
        id="pointcloud-state-region"
        role="status"
        aria-label="Point cloud state narration"
        aria-live="polite"
        aria-atomic="false"
        style={{
          position: 'absolute',
          width: tokens.visuallyHidden.size,
          height: tokens.visuallyHidden.size,
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        {stateNarration}
      </div>
    </div>
  );
}
